// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
// import { isMobile, isTablet } from 'react-device-detect';
import { node } from 'prop-types';
import useSWR, { useSWRConfig } from 'swr';
import merge from 'plain-object-merge';
import omit from 'lodash/omit';
// import debounce from 'lodash/debounce';
import { stringify } from 'query-string';
import noop from 'lodash/noop';

import useAsyncError from 'Hooks/useAsyncError';
import useRouter from 'Hooks/useRouter';
import { API_PRODUCTS } from 'Constants';
import useFetch from 'Hooks/useFetch';
import { UserContext } from 'Contexts/UserContext';

export const ProductsContext = createContext({
  data: null,
  error: undefined,
  isLoading: false,
  updateProduct: noop,
  updateProducts: noop
});

const LATEST_PRODUCTS = `${ API_PRODUCTS }_latest`;
export const LATEST_PATH = `${ API_PRODUCTS }_latest_path`;

const ProductsProvider = memo(({ children }) => {
  const loaded = useRef(false);
  const user = useContextSelector(UserContext, data => data?.data || {});
  const fetch = useFetch();
  const throwError = useAsyncError();
  const { location, query } = useRouter();
  const { cache } = useSWRConfig();
  const [loading, setLoading] = useState(false);
  // const [shouldRetry, setShouldRetry] = useState(true);

  const composePath = () => {
    if (
      location.pathname.match(/^\/products\/[\w+?!'"_\\-]+\/pd\/[\w\d]{32}\/?/g)
      || !location.pathname.match(/^\/products/)
      || !user?.email
      || !user?.loggedIn
    ) return null;
    const pathName = location.pathname.includes('/products')
      ? location.pathname
      : '/products';

    const payload = merge([{}, {
      ...omit(query, ['ref', 'filtering']),
      vat: user.withVat,
      currency: user.defaultCurrency
    }]);

    // if (
    //   typeof document !== 'undefined'
    //   && !('count' in payload)
    // ) {
    //   const clientWidth = document?.documentElement?.clientWidth;
    //   if (clientWidth <= 540) {
    //     payload.count = 10;
    //   }
    //   if (clientWidth > 540 && clientWidth <= 1080) {
    //     payload.count = 15;
    //   }
    // }

    let path = `/api${ pathName }`;
    const hasQueryParams = Object.keys(payload).length;

    if (hasQueryParams) {
      path += `?${ stringify(payload) }`;
    }

    if (
      user?.email
      && user?.loggedIn
    ) {
      path += `${ hasQueryParams ? '&' : '?' }uid=${ user?.email }`;
    }

    return path;
  };

  if (!loaded.current && !cache.get(LATEST_PATH)) {
    cache.set(LATEST_PATH, composePath());
  }

  // const composeNewPath = useCallback(
  //   composePath,
  //   [
  //     user?.email,
  //     user?.loggedIn,
  //     location.pathname,
  //     query,
  //     user?.defaultCurrency
  //     // document?.documentElement?.clientWidth
  //   ]
  // );

  // const path = composeNewPath();
  // if (
  //   cacheProducts
  //   && !cache.has(LATEST_PRODUCTS)
  // ) {
  // if (!user?.email) {
  //   cache.set(path, cacheProducts);
  // } else {
  //   cache.set(`${ path }?uid=${ user.email }`, cacheProducts);
  // }
  // cache.set(LATEST_PATH, composePath());
  // cache.set(LATEST_PRODUCTS, cacheProducts);
  // }

  // const fetchProductsWrapper = async apiPath => {
  //   const data = await fetch(apiPath, { cacheKey: API_PRODUCTS });
  //   const pathCache = cache.get(apiPath);
  //
  //   if (!data?.products) {
  //     if (pathCache) {
  //       return {
  //         ...pathCache,
  //         error: data?.error || 'error fetching data'
  //       };
  //     }
  //
  //     const latest = cache.get(LATEST_PRODUCTS);
  //     if (latest) {
  //       return {
  //         ...latest,
  //         error: data?.error || 'error fetching data'
  //       };
  //     }
  //   }
  //
  //   if (!dequal(data, pathCache)) {
  //     cache.set(apiPath, {
  //       ...data,
  //       latestFetch: Date.now()
  //     });
  //     // cache.set(LATEST_PATH, apiPath);
  //     cache.set(LATEST_PRODUCTS, data);
  //     return data;
  //   }
  //
  //   return pathCache;
  // };

  /**
   * Wrapper to fetch and cache products
   * Also updates swr response if data !== cache, if cache provided
   * @param {string} apiPath
   */

  // const debouncedFetchProductsWrapper = useCallback(
  //   debounce(fetchProductsWrapper, 350, { leading: true, trailing: false }),
  //   []
  // );

  // useEffect(() => {
  //   let timeout;
  //   if (!shouldRetry) {
  //     timeout = setTimeout(() => {
  //       setShouldRetry(true);
  //     }, 1000 * 60);
  //   }
  //   return () => {
  //     clearTimeout(timeout);
  //   };
  // }, [shouldRetry]);

  const response = useSWR(
    composePath,
    async apiPath => {
      const cachedProducts = cache.get(apiPath);
      setLoading(true);

      const hasCachedProducts = cachedProducts?.products?.length > 0;

      if (
        hasCachedProducts
        && cachedProducts?.latestFetch
        && cachedProducts?.latestFetch < Date.now() + (1000 * 60 * 10)
      ) {
        setLoading(false);
        cache.set(LATEST_PATH, apiPath);
        cache.set(LATEST_PRODUCTS, cachedProducts);
        return cachedProducts;
      }

      let data;

      try {
        data = await fetch(apiPath, { cacheKey: API_PRODUCTS });
      } catch (err) {
        throwError(err);
      }

      if (data === null) {
        setLoading(false);
        return null;
      }

      const filterParams = omit(query, ['page', 'count', 'order_by', 'order_direction', 'ref']);
      const isSearchPath = apiPath?.includes('/search/');
      const hasActiveFilters = Object.keys(filterParams).length > 0 || isSearchPath;

      if (!data?.products || data.products.length === 0) {
        if (hasActiveFilters) {
          cache.set(LATEST_PATH, apiPath);
          cache.set(LATEST_PRODUCTS, data);
          setLoading(false);
          return data;
        }

        if (hasCachedProducts) {
          cache.set(LATEST_PATH, apiPath);
          cache.set(LATEST_PRODUCTS, data);
          setLoading(false);
          return {
            ...cachedProducts,
            error: data?.error || 'error fetching data'
          };
        }

        const latestProducts = cache.get(LATEST_PRODUCTS);
        if (latestProducts?.products?.length > 0) {
          setLoading(false);
          return {
            ...latestProducts,
            error: data?.error || 'error fetching data'
          };
        }
        setLoading(false);
        return data;
      }

      cache.set(LATEST_PATH, apiPath);
      cache.set(LATEST_PRODUCTS, data);

      if (data) {
        setLoading(false);
      }

      return {
        ...data,
        latestFetch: Date.now()
      };
    },
    {
      shouldRetryOnError: true,
      errorRetryCount: 2,
      // only use fallback data if it has products
      fallbackData: () => {
        const latest = cache.get(LATEST_PRODUCTS);
        return latest?.products?.length > 0 ? latest : undefined;
      }
    }
  );

  // useEffect(() => {
  //   if (
  //     user?.loggedIn
  //     // && !shouldRetry
  //   ) {
  //     response.mutate();
  //   }
  // }, [user?.loggedIn]);

  useEffect(() => {
    const newPath = composePath();
    if (
      newPath
      && newPath !== cache.get(LATEST_PATH)
    ) {
      cache.set(LATEST_PATH, newPath);
      response.mutate();
    }
  }, [
    location.pathname,
    query
  ]);

  useEffect(() => {
    if (user?.loggedIn) {
      response.mutate();
    }
  }, [
    user?.loggedIn,
    user?.defaultCurrency,
    user?.withVat,
  ]);

  /**
   * Handle multiple products at once
   * @param {array} products
   */
  const handleUpdateProducts = (products = []) => {
    response.mutate(data => {
      const existing = products.some(prd => (data?.products || []).find(p => p.id === prd.id));
      if (!existing) return data;
      return {
        ...(data || {}),
        products: (data.products || []).reduce((acc, prod) => {
          const product = products.find(p => p.id === prod.id);
          if (product) {
            acc.push({ ...prod, ...product });
          } else {
            acc.push(prod);
          }
          return acc;
        }, [])
      };
    }, false);
  };

  /**
   * Updates a product in the cache
   * @param {object} product
   */
  const handleUpdateProduct = product => {
    if (product) {
      // let cachedProducts = cache.get(path);
      // if (!cachedProducts) {
      //   cachedProducts = !response.data ? cache.get(LATEST_PRODUCTS) : response.data;
      // }
      // cache.set(path, updatedData);
      response.mutate(data => {
        const productIdx = (data?.products || []).findIndex(prod => prod.id === product.id);
        if (productIdx === -1) return data;

        return {
          ...(data || {}),
          products: (data?.products || []).reduce((acc, prod) => {
            if (prod.id === product.id) {
              acc.push({ ...prod, ...product });
            } else {
              acc.push(prod);
            }
            return acc;
          }, [])
        };
      }, false);
    }
  };

  return (
    <ProductsContext.Provider
      value={ {
        data: !response.data
          ? cache.get(LATEST_PRODUCTS)
          : response.data,
        error: response.error,
        isLoading: response.isValidating || loading,
        updateProducts: handleUpdateProducts,
        updateProduct: handleUpdateProduct,
      } }
    >
      { children }
    </ProductsContext.Provider>
  );
});

ProductsProvider.propTypes = {
  children: node
};

ProductsProvider.displayName = 'ProductsProvider';

export default ProductsProvider;
